import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { request } from "app/network/networkManager";
import Swal from "sweetalert2";

interface ReorderCategoryDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    categories: Array<{ seqId: number; categoryName: string; sortOrder: number; useYn: string; categoryCode: string; categoryDesc: string }>;
    darkMode?: boolean;
}

interface SortableItemProps {
    id: string;
    title: string;
    darkMode: boolean;
}

function SortableItem({ id, title, darkMode }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                border: darkMode
                    ? "1px solid rgba(255, 255, 255, 0.2)"
                    : "1px solid rgba(0, 0, 0, 0.2)",
                backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)",
                cursor: "grab",
                "&:active": {
                    cursor: "grabbing",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                }}
            />
            <MDTypography
                variant="button"
                fontWeight="regular"
                sx={{
                    color: darkMode
                        ? "rgba(255, 255, 255, 0.8)"
                        : "#344767",
                }}
            >
                {title}
            </MDTypography>
        </MDBox>
    );
}

function ReorderCategoryDialog({ open, onClose, onSuccess, categories, darkMode = false }: ReorderCategoryDialogProps) {
    const [orderedCategories, setOrderedCategories] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Initialize list when open or categories change
    useEffect(() => {
        if (open && categories.length > 0) {
            // Sort by current SortOrder (or default order from API)
            const sorted = [...categories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
            setOrderedCategories(sorted);
        }
    }, [open, categories]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setOrderedCategories((items) => {
                const oldIndex = items.findIndex((item) => `cat-${item.seqId}` === active.id);
                const newIndex = items.findIndex((item) => `cat-${item.seqId}` === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Sequential Update
            // Note: Since there is no Bulk Update API, we update individually.
            // In a real production scenario with many items, a Batch Update API is recommended.
            for (let i = 0; i < orderedCategories.length; i++) {
                const cat = orderedCategories[i];
                const newOrder = i + 1;

                if (cat.sortOrder !== newOrder) {
                    await request("GiftCategory.Update", {
                        paramz: {
                            categoryName: cat.categoryName,
                            sortOrder: newOrder,
                            isActive: cat.useYn === "Y"
                        },
                        pathParams: { id: cat.seqId.toString() }
                    });
                }
            }

            Swal.fire({
                title: "성공",
                text: "카테고리 순서가 변경되었습니다.",
                icon: "success",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("Failed to save order:", error);
            Swal.fire({
                title: "오류",
                text: "순서 저장 중 오류가 발생했습니다.",
                icon: "error",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ zIndex: 2000 }}
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? "#1a2035" : "#fff",
                    color: darkMode ? "#fff" : "#000",
                },
            }}
        >
            <DialogTitle>카테고리 순서 변경</DialogTitle>
            <DialogContent>
                <MDBox pt={2}>
                    <MDTypography
                        variant="caption"
                        fontWeight="regular"
                        sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }}
                        mb={2}
                        display="block"
                    >
                        드래그하여 카테고리 순서를 변경하세요.
                    </MDTypography>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={orderedCategories.map((c) => `cat-${c.seqId}`)}
                            strategy={verticalListSortingStrategy}
                        >
                            {orderedCategories.map((c) => (
                                <SortableItem
                                    key={c.seqId}
                                    id={`cat-${c.seqId}`}
                                    title={c.categoryName}
                                    darkMode={darkMode}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="outlined" color="secondary">
                    취소
                </MDButton>
                <MDButton
                    onClick={handleSave}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? "저장 중..." : "저장"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

export default ReorderCategoryDialog;
